import { useState, useEffect } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import api from '../../lib/api';
import { Button } from '../../components/ui/button';
import { Input } from '../../components/ui/input';
import { Label } from '../../components/ui/label';
import { Textarea } from '../../components/ui/textarea';
import { Badge } from '../../components/ui/badge';
import { Card, CardContent, CardHeader, CardTitle } from '../../components/ui/card';
import FileUpload from '../../components/FileUpload';
import { ArrowLeft, Plus, Trash2, Save, Eye, EyeOff, Star } from 'lucide-react';
import { toast } from 'sonner';

const EMPTY = {
  name: '', variety: '', category: '', subcategory: '', description: '', growing_notes: '',
  price_per_kg: '', original_price: '', discount_percentage: '',
  stock: '', total_production: '', min_order_qty: '1',
  expected_delivery_days: '2', delivery_days: '2',
  special_tag: '', refund_policy: '', harvest_date: '',
  images: [], variants: [{ name: 'Small Box', weight: '', price: '' }],
  lab_reports: [], lab_report_url: '',
  faq: [], status: 'draft',
};

export default function AdminProductEdit() {
  const { id } = useParams();
  const navigate = useNavigate();
  const isEdit = !!id;
  const [product, setProduct] = useState(EMPTY);
  const [categories, setCategories] = useState([]);
  const [farmers, setFarmers] = useState([]);
  const [links, setLinks] = useState([]); // farmer_products
  const [newLink, setNewLink] = useState({ farmer_id: '', procurement_price: '', expected_harvest_date: '', approximate_stock_kg: '' });
  const [reviews, setReviews] = useState([]);
  const [saving, setSaving] = useState(false);
  const [loading, setLoading] = useState(isEdit);
  const [showPreview, setShowPreview] = useState(true);

  useEffect(() => {
    api.get('/categories').then(r => setCategories(r.data || []));
    api.get('/admin/farmers').then(r => setFarmers(r.data || []));
    if (isEdit) {
      Promise.all([
        api.get(`/products/${id}`).then(r => {
          const p = r.data;
          setProduct({ ...EMPTY, ...p, variants: p.variants?.length ? p.variants : EMPTY.variants });
        }),
        api.get(`/admin/farmer-products/product/${id}`).then(r => setLinks(r.data || [])).catch(() => {}),
        api.get(`/admin/reviews/product/${id}`).then(r => setReviews(r.data || [])).catch(() => {}),
      ]).finally(() => setLoading(false));
    }
  }, [id, isEdit]);

  const save = async (publish = false) => {
    if (!product.name || !product.category) { toast.error('Name and category required'); return; }
    setSaving(true);
    try {
      const payload = {
        ...product,
        price_per_kg: parseFloat(product.price_per_kg) || 0,
        original_price: parseFloat(product.original_price) || 0,
        discount_percentage: parseInt(product.discount_percentage) || 0,
        stock: parseFloat(product.stock) || 0,
        total_production: parseFloat(product.total_production) || 0,
        min_order_qty: parseFloat(product.min_order_qty) || 1,
        expected_delivery_days: parseInt(product.expected_delivery_days) || 2,
        delivery_days: parseInt(product.delivery_days || product.expected_delivery_days) || 2,
        variants: product.variants.filter(v => v.name).map(v => ({ ...v, price: parseFloat(v.price) || 0 })),
        images: (product.images || []).filter(Boolean),
        lab_reports: (product.lab_reports || []).filter(r => r && r.url),
        faq: (product.faq || []).filter(f => (f.question || f.q) && (f.answer || f.a)),
        status: publish ? 'active' : product.status,
      };
      if (isEdit) {
        await api.put(`/admin/products/${id}`, payload);
        toast.success(publish ? 'Product published!' : 'Product updated successfully');
        // Refresh local state from server so edit form and list show updated values
        const r = await api.get(`/products/${id}`);
        setProduct({ ...EMPTY, ...r.data, variants: r.data.variants?.length ? r.data.variants : EMPTY.variants });
      } else {
        const res = await api.post('/admin/products', payload);
        toast.success('Product created');
        navigate(`/admin/products/${res.data.product_id}/edit`);
      }
    } catch (err) {
      const msg = err.response?.data?.detail || err.message || 'Save failed';
      toast.error(typeof msg === 'string' ? msg : 'Save failed. Check console for details.');
      console.error('Product save error:', err.response?.data || err);
    }
    finally { setSaving(false); }
  };

  // Auto-calc discount %
  const onPriceChange = (field, value) => {
    const next = { ...product, [field]: value };
    const pr = parseFloat(field === 'price_per_kg' ? value : next.price_per_kg) || 0;
    const op = parseFloat(field === 'original_price' ? value : next.original_price) || 0;
    if (op > 0 && pr > 0 && op > pr) {
      next.discount_percentage = Math.round(((op - pr) / op) * 100);
    } else if (op <= 0) {
      next.discount_percentage = 0;
    }
    setProduct(next);
  };

  const addLink = async () => {
    if (!newLink.farmer_id) { toast.error('Pick a farmer'); return; }
    try {
      await api.post('/admin/farmer-products', {
        farmer_id: newLink.farmer_id, product_id: id,
        procurement_price: parseFloat(newLink.procurement_price) || 0,
        expected_harvest_date: newLink.expected_harvest_date || '',
        approximate_stock_kg: parseFloat(newLink.approximate_stock_kg) || 0,
        is_active: true,
      });
      toast.success('Farmer linked');
      const r = await api.get(`/admin/farmer-products/product/${id}`);
      setLinks(r.data || []);
      setNewLink({ farmer_id: '', procurement_price: '', expected_harvest_date: '', approximate_stock_kg: '' });
    } catch { toast.error('Failed'); }
  };
  const updateLink = async (linkId, patch) => {
    try {
      await api.put(`/admin/farmer-products/${linkId}`, patch);
      const r = await api.get(`/admin/farmer-products/product/${id}`);
      setLinks(r.data || []);
    } catch { toast.error('Update failed'); }
  };
  const removeLink = async (linkId) => {
    if (!window.confirm('Remove this farmer link?')) return;
    await api.delete(`/admin/farmer-products/${linkId}`);
    const r = await api.get(`/admin/farmer-products/product/${id}`);
    setLinks(r.data || []);
  };
  const deleteReview = async (rid) => {
    if (!window.confirm('Delete this review?')) return;
    await api.delete(`/admin/reviews/${rid}`);
    const r = await api.get(`/admin/reviews/product/${id}`);
    setReviews(r.data || []);
  };

  if (loading) return <div className="p-20 flex justify-center"><div className="animate-spin h-8 w-8 border-2 border-primary border-t-transparent rounded-full" /></div>;

  const cat = categories.find(c => c.slug === product.category);
  const availableFarmerIds = new Set(links.map(l => l.farmer_id));

  return (
    <div data-testid="admin-product-edit-page" className="p-6 lg:p-8">
      <div className="flex items-center gap-4 mb-6 flex-wrap">
        <Button variant="ghost" size="icon" onClick={() => navigate('/admin/products')} className="rounded-full"><ArrowLeft className="h-4 w-4" /></Button>
        <div className="flex-1">
          <h1 className="text-2xl font-serif font-medium">{isEdit ? 'Edit Product' : 'New Product'}</h1>
          {product.name && <p className="text-sm text-muted-foreground">{product.name}</p>}
        </div>
        <Button variant="outline" size="sm" onClick={() => setShowPreview(!showPreview)} className="rounded-full gap-2">
          {showPreview ? <EyeOff className="h-3.5 w-3.5" /> : <Eye className="h-3.5 w-3.5" />} Preview
        </Button>
        <Button data-testid="save-draft-btn" variant="outline" onClick={() => save(false)} disabled={saving} className="rounded-full">
          <Save className="h-4 w-4 mr-2" />{saving ? 'Saving...' : 'Save Draft'}
        </Button>
        <Button data-testid="publish-product-btn" onClick={() => save(true)} disabled={saving} className="rounded-full">
          {product.status === 'active' ? 'Save Changes' : 'Publish'}
        </Button>
      </div>

      <div className={`grid ${showPreview ? 'grid-cols-1 lg:grid-cols-3' : 'grid-cols-1'} gap-6 max-w-7xl`}>
        <div className={showPreview ? 'lg:col-span-2 space-y-4' : 'space-y-4'}>
          <Card className="rounded-xl">
            <CardHeader><CardTitle className="text-base">Basic Info</CardTitle></CardHeader>
            <CardContent className="space-y-3">
              <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
                <div><Label>Name *</Label><Input data-testid="product-name-input" value={product.name} onChange={e => setProduct({...product, name: e.target.value})} /></div>
                <div><Label>Variety</Label><Input value={product.variety} onChange={e => setProduct({...product, variety: e.target.value})} /></div>
                <div>
                  <Label>Category *</Label>
                  <select value={product.category} onChange={e => setProduct({...product, category: e.target.value, subcategory: ''})}
                    className="w-full h-10 px-3 rounded-lg border border-input text-sm bg-background">
                    <option value="">Select category</option>
                    {categories.map(c => <option key={c.slug} value={c.slug}>{c.name}</option>)}
                  </select>
                </div>
                <div>
                  <Label>Subcategory</Label>
                  <select value={product.subcategory} onChange={e => setProduct({...product, subcategory: e.target.value})}
                    className="w-full h-10 px-3 rounded-lg border border-input text-sm bg-background" disabled={!cat?.subcategories?.length}>
                    <option value="">None</option>
                    {(cat?.subcategories || []).map(s => <option key={s.slug} value={s.slug}>{s.name}</option>)}
                  </select>
                </div>
              </div>
              <div><Label>Description</Label><Textarea rows={3} value={product.description} onChange={e => setProduct({...product, description: e.target.value})} /></div>
              <div><Label>Growing Notes</Label><Textarea rows={2} value={product.growing_notes} onChange={e => setProduct({...product, growing_notes: e.target.value})} /></div>
            </CardContent>
          </Card>

          <Card className="rounded-xl">
            <CardHeader><CardTitle className="text-base">Pricing & Delivery</CardTitle></CardHeader>
            <CardContent className="space-y-3">
              <div className="grid grid-cols-2 sm:grid-cols-3 gap-3">
                <div><Label>Price per kg *</Label><Input data-testid="product-price-input" type="number" value={product.price_per_kg} onChange={e => onPriceChange('price_per_kg', e.target.value)} /></div>
                <div><Label>Original Price</Label><Input type="number" value={product.original_price} onChange={e => onPriceChange('original_price', e.target.value)} /></div>
                <div>
                  <Label>Discount %</Label>
                  <Input data-testid="product-discount-input" type="number" value={product.discount_percentage} onChange={e => setProduct({...product, discount_percentage: e.target.value})} />
                  <p className="text-[10px] text-muted-foreground mt-0.5">Auto-calcs from original price</p>
                </div>
                <div><Label>Stock (kg) *</Label><Input type="number" value={product.stock} onChange={e => setProduct({...product, stock: e.target.value})} /></div>
                <div><Label>Total Production (kg)</Label><Input type="number" value={product.total_production} onChange={e => setProduct({...product, total_production: e.target.value})} /></div>
                <div><Label>Delivery Days</Label>
                  <Input data-testid="product-delivery-days-input" type="number" min="0" value={product.delivery_days || product.expected_delivery_days} onChange={e => setProduct({...product, delivery_days: e.target.value, expected_delivery_days: e.target.value})} />
                  <p className="text-[10px] text-muted-foreground mt-0.5">0 = Same day, 1 = Next day</p>
                </div>
                <div><Label>Min Order Qty</Label><Input type="number" value={product.min_order_qty} onChange={e => setProduct({...product, min_order_qty: e.target.value})} /></div>
                <div><Label>Harvest Date</Label><Input type="date" value={product.harvest_date} onChange={e => setProduct({...product, harvest_date: e.target.value})} /></div>
                <div>
                  <Label>Special Tag</Label>
                  <Input data-testid="product-special-tag-input" value={product.special_tag} onChange={e => setProduct({...product, special_tag: e.target.value})} placeholder="Carbide Free / Organic / Premium" />
                </div>
              </div>
            </CardContent>
          </Card>

          <Card className="rounded-xl">
            <CardHeader><CardTitle className="text-base">Media</CardTitle></CardHeader>
            <CardContent className="space-y-3">
              <div><Label>Images (drag & drop, multiple)</Label><FileUpload folder="products" multiple existingFiles={product.images} onUpload={urls => setProduct({...product, images: urls})} label="Upload product images" /></div>
              <div>
                <Label>Lab Reports (with labels)</Label>
                <div className="space-y-2 mt-1">
                  {(product.lab_reports || []).map((r, i) => (
                    <div key={i} className="flex gap-2 items-center p-2 bg-secondary/30 rounded-xl">
                      <Input placeholder="Report name" value={r.name || ''}
                        onChange={e => { const arr = [...product.lab_reports]; arr[i] = {...arr[i], name: e.target.value}; setProduct({...product, lab_reports: arr}); }} className="flex-1 text-sm" />
                      {r.url && <a href={r.url} target="_blank" rel="noopener noreferrer" className="text-xs text-primary hover:underline">View</a>}
                      <Button variant="ghost" size="icon" onClick={() => setProduct({...product, lab_reports: product.lab_reports.filter((_, j) => j !== i)})}><Trash2 className="h-4 w-4" /></Button>
                    </div>
                  ))}
                  <FileUpload folder="products/reports" accept=".pdf,image/*"
                    onUpload={url => { if (!url) return; setProduct({...product, lab_reports: [...(product.lab_reports || []), { name: `Report ${(product.lab_reports || []).length + 1}`, url }]}); }}
                    existingFiles={[]} label="Upload another report" />
                </div>
              </div>
            </CardContent>
          </Card>

          <Card className="rounded-xl">
            <CardHeader><CardTitle className="text-base">Variants (Box Sizes)</CardTitle></CardHeader>
            <CardContent className="space-y-3">
              {product.variants.map((v, i) => (
                <div key={i} className="grid grid-cols-[1fr_1fr_1fr_auto] gap-2">
                  <Input placeholder="Label" value={v.name} onChange={e => { const arr = [...product.variants]; arr[i] = {...arr[i], name: e.target.value}; setProduct({...product, variants: arr}); }} />
                  <Input placeholder="Weight" value={v.weight || ''} onChange={e => { const arr = [...product.variants]; arr[i] = {...arr[i], weight: e.target.value}; setProduct({...product, variants: arr}); }} />
                  <Input placeholder="Price" type="number" value={v.price} onChange={e => { const arr = [...product.variants]; arr[i] = {...arr[i], price: e.target.value}; setProduct({...product, variants: arr}); }} />
                  <Button variant="ghost" size="icon" onClick={() => setProduct({...product, variants: product.variants.filter((_, j) => j !== i)})}><Trash2 className="h-4 w-4" /></Button>
                </div>
              ))}
              <Button variant="ghost" size="sm" onClick={() => setProduct({...product, variants: [...product.variants, {name:'', weight:'', price:''}]})}><Plus className="h-3 w-3 mr-1" /> Add Variant</Button>
            </CardContent>
          </Card>

          <Card className="rounded-xl">
            <CardHeader><CardTitle className="text-base">Refund Policy & Product FAQs</CardTitle></CardHeader>
            <CardContent className="space-y-3">
              <div>
                <Label>Refund Policy</Label>
                <Textarea rows={3} value={product.refund_policy} onChange={e => setProduct({...product, refund_policy: e.target.value})}
                  placeholder="Default: We take quality seriously. If product does not meet standards, contact us within 24 hours with a photo..." />
              </div>
              <div>
                <Label>Product FAQs</Label>
                {(product.faq || []).map((f, i) => (
                  <div key={i} className="grid grid-cols-[1fr_2fr_auto] gap-2 mt-2">
                    <Input placeholder="Question" value={f.question || f.q || ''} onChange={e => { const arr = [...product.faq]; arr[i] = {...arr[i], question: e.target.value}; setProduct({...product, faq: arr}); }} />
                    <Input placeholder="Answer" value={f.answer || f.a || ''} onChange={e => { const arr = [...product.faq]; arr[i] = {...arr[i], answer: e.target.value}; setProduct({...product, faq: arr}); }} />
                    <Button variant="ghost" size="icon" onClick={() => setProduct({...product, faq: product.faq.filter((_, j) => j !== i)})}><Trash2 className="h-4 w-4" /></Button>
                  </div>
                ))}
                <Button variant="ghost" size="sm" className="mt-2" onClick={() => setProduct({...product, faq: [...(product.faq || []), {question:'', answer:''}]})}><Plus className="h-3 w-3 mr-1" /> Add FAQ</Button>
              </div>
            </CardContent>
          </Card>

          {isEdit && (
            <Card className="rounded-xl">
              <CardHeader><CardTitle className="text-base">Linked Farmers (many-to-many)</CardTitle></CardHeader>
              <CardContent className="space-y-3">
                {links.length === 0 && <p className="text-xs text-muted-foreground">No farmers linked yet.</p>}
                {links.map((link) => (
                  <div key={link.id} data-testid={`farmer-link-${link.id}`} className="grid grid-cols-1 sm:grid-cols-[180px_1fr_1fr_1fr_auto_auto] gap-2 p-3 bg-secondary/30 rounded-xl items-center">
                    <div className="text-sm font-medium truncate">{link.farmer?.name || link.farmer_id}</div>
                    <Input placeholder="₹/kg" type="number" value={link.procurement_price || ''} onChange={e => updateLink(link.id, { procurement_price: parseFloat(e.target.value) || 0 })} />
                    <Input placeholder="Harvest" type="date" value={link.expected_harvest_date || ''} onChange={e => updateLink(link.id, { expected_harvest_date: e.target.value })} />
                    <Input placeholder="kg" type="number" value={link.approximate_stock_kg || ''} onChange={e => updateLink(link.id, { approximate_stock_kg: parseFloat(e.target.value) || 0 })} />
                    <Button variant={link.is_active ? 'default' : 'outline'} size="sm" className="rounded-full h-8 text-xs" onClick={() => updateLink(link.id, { is_active: !link.is_active })}>
                      {link.is_active ? 'Active' : 'Inactive'}
                    </Button>
                    <Button variant="ghost" size="icon" onClick={() => removeLink(link.id)}><Trash2 className="h-4 w-4 text-destructive" /></Button>
                  </div>
                ))}
                <div className="grid grid-cols-1 sm:grid-cols-[180px_1fr_1fr_1fr_auto] gap-2 pt-3 border-t">
                  <select value={newLink.farmer_id} onChange={e => setNewLink({...newLink, farmer_id: e.target.value})}
                    className="h-10 px-3 rounded-lg border border-input text-sm bg-background">
                    <option value="">Select farmer</option>
                    {farmers.filter(f => !availableFarmerIds.has(f.farmer_id)).map(f => <option key={f.farmer_id} value={f.farmer_id}>{f.name}</option>)}
                  </select>
                  <Input placeholder="Procurement ₹/kg" type="number" value={newLink.procurement_price} onChange={e => setNewLink({...newLink, procurement_price: e.target.value})} />
                  <Input placeholder="Harvest date" type="date" value={newLink.expected_harvest_date} onChange={e => setNewLink({...newLink, expected_harvest_date: e.target.value})} />
                  <Input placeholder="Approx kg" type="number" value={newLink.approximate_stock_kg} onChange={e => setNewLink({...newLink, approximate_stock_kg: e.target.value})} />
                  <Button data-testid="add-farmer-link-btn" onClick={addLink} className="rounded-full" size="sm"><Plus className="h-3 w-3 mr-1" /> Link</Button>
                </div>
              </CardContent>
            </Card>
          )}

          {isEdit && reviews.length > 0 && (
            <Card className="rounded-xl">
              <CardHeader><CardTitle className="text-base">Customer Reviews ({reviews.length})</CardTitle></CardHeader>
              <CardContent className="space-y-2">
                {reviews.map(r => (
                  <div key={r.id} className="flex items-start gap-3 p-3 bg-secondary/30 rounded-xl">
                    <div className="flex-1">
                      <div className="flex items-center gap-2">
                        <span className="text-sm font-medium">{r.customer_name}</span>
                        <div className="flex gap-0.5">
                          {Array.from({ length: 5 }).map((_, i) => (
                            <Star key={i} className={`h-3 w-3 ${i < r.rating ? 'text-amber-500 fill-amber-500' : 'text-muted-foreground/30'}`} />
                          ))}
                        </div>
                      </div>
                      <p className="text-xs text-muted-foreground mt-1">{r.review_text}</p>
                    </div>
                    <Button variant="ghost" size="icon" onClick={() => deleteReview(r.id)}><Trash2 className="h-4 w-4 text-destructive" /></Button>
                  </div>
                ))}
              </CardContent>
            </Card>
          )}
        </div>

        {/* LIVE PREVIEW */}
        {showPreview && (
          <div className="lg:col-span-1">
            <div className="bg-background rounded-xl border p-4 sticky top-4 max-h-[85vh] overflow-y-auto">
              <p className="text-xs font-semibold uppercase tracking-wider text-muted-foreground mb-3">Customer View Preview</p>
              <div className="relative aspect-square rounded-2xl bg-muted overflow-hidden mb-3">
                {product.images?.[0] && <img src={product.images[0]} alt="" className="w-full h-full object-cover" />}
                {product.discount_percentage > 0 && <div className="absolute top-3 left-3 bg-neutral-900 text-white text-[10px] font-semibold px-2 py-0.5 rounded-full">{product.discount_percentage}% Off</div>}
                {product.special_tag && <div className="absolute top-3 right-3 bg-primary text-primary-foreground text-[10px] font-semibold px-2 py-0.5 rounded-full">{product.special_tag}</div>}
              </div>
              <h4 className="font-semibold text-sm">{product.name || 'Product Name'}</h4>
              <div className="flex items-center gap-1.5 mt-1.5">
                <Badge variant="secondary" className="rounded-full text-[10px]">{product.category || 'category'}</Badge>
                {(product.delivery_days == 0) && <Badge className="rounded-full text-[10px] bg-primary/10 text-primary">Same Day</Badge>}
                {(product.delivery_days == 1) && <Badge className="rounded-full text-[10px] bg-primary/10 text-primary">Next Day</Badge>}
              </div>
              <p className="text-xs text-muted-foreground mt-2 line-clamp-3">{product.description}</p>
              <div className="flex items-baseline gap-1.5 mt-3">
                {product.original_price > 0 && <span className="text-xs text-muted-foreground line-through">{'\u20B9'}{product.original_price}</span>}
                <span className="text-lg font-semibold">{'\u20B9'}{product.price_per_kg || 0}</span>
              </div>
              {links.length > 0 && (
                <div className="mt-3 pt-3 border-t">
                  <p className="text-[10px] font-semibold uppercase text-muted-foreground mb-2">Sourced from</p>
                  {links.filter(l => l.is_active).slice(0, 3).map(l => (
                    <p key={l.id} className="text-xs text-muted-foreground">{l.farmer?.name}</p>
                  ))}
                </div>
              )}
            </div>
          </div>
        )}
      </div>
    </div>
  );
}
